import { useEffect, useState } from "react";

interface DexScreenerPair {
  chainId: string;
  dexId: string;
  url: string;
  pairAddress: string;
  baseToken: {
    address: string;
    name: string;
    symbol: string;
  };
  quoteToken: {
    address: string;
    name: string;
    symbol: string;
  };
  priceNative: string;
  priceUsd: string;
  volume: {
    h24: number;
    h6: number;
    h1: number;
    m5: number;
  };
  priceChange: {
    h24: number;
    h6: number;
    h1: number;
    m5: number;
  };
  liquidity: {
    usd: number;
    base: number;
    quote: number;
  };
  txns: {
    h24: {
      buys: number;
      sells: number;
    };
  };
}

interface TokenPriceData {
  price: number;
  change24h: number;
  volume24h: number;
  liquidity: number;
  txns24h: {
    buys: number;
    sells: number;
  };
}

export function useTokenPrice(tokenAddress?: string, chainId: string = "berachain") {
  const [data, setData] = useState<TokenPriceData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Default to HENLO token address on Berachain if not provided
  // Correct address found from DexScreener search
  const address = tokenAddress || "0xb2F776e9c1C926C4b2e54182Fac058dA9Af0B6A5";

  useEffect(() => {
    const fetchPrice = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // DexScreener API v1 endpoint - requires chainId
        // Try the tokens endpoint first, then fall back to token-pairs
        let response = await fetch(`https://api.dexscreener.com/tokens/v1/${chainId}/${address}`, {
          headers: {
            Accept: "application/json",
          },
        });

        let result = await response.json();

        // If tokens endpoint doesn't work, try token-pairs endpoint
        if (!response.ok || (Array.isArray(result) && result.length === 0)) {
          response = await fetch(
            `https://api.dexscreener.com/token-pairs/v1/${chainId}/${address}`,
            {
              headers: {
                Accept: "application/json",
              },
            },
          );

          if (response.ok) {
            result = await response.json();
          }
        }

        if (!response.ok) {
          throw new Error("Failed to fetch price data");
        }

        // The v1 endpoint returns an array of pairs directly
        const pairs: DexScreenerPair[] = Array.isArray(result) ? result : result.pairs || [];

        // Get the first pair (usually the one with highest liquidity)
        const pair = pairs[0];

        if (!pair) {
          // If no DEX data, return mock data
          setData({
            price: 0.42,
            change24h: 6.9,
            volume24h: 420000,
            liquidity: 1000000,
            txns24h: {
              buys: 69,
              sells: 42,
            },
          });
        } else {
          // Handle the response format - use h24 which is the standard format from DexScreener
          const txns24h = pair.txns?.h24 || { buys: 0, sells: 0 };
          const volume24h = pair.volume?.h24 || 0;
          const priceChange24h = pair.priceChange?.h24 || 0;

          setData({
            price: parseFloat(pair.priceUsd || "0"),
            change24h: priceChange24h,
            volume24h: volume24h,
            liquidity: pair.liquidity?.usd || 0,
            txns24h: {
              buys: txns24h.buys || 0,
              sells: txns24h.sells || 0,
            },
          });
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to fetch price");

        // Fallback to mock data
        setData({
          price: 0.42,
          change24h: 6.9,
          volume24h: 420000,
          liquidity: 1000000,
          txns24h: {
            buys: 69,
            sells: 42,
          },
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchPrice();

    // Refresh every 30 seconds
    const interval = setInterval(fetchPrice, 30000);

    return () => clearInterval(interval);
  }, [address, chainId]);

  return { data, isLoading, error };
}
